import { action, makeObservable, observable } from "mobx";
import React from "react";
import { ButtonTypes } from "../../../../../../components/ui/Button";
import { Icon, IconSymbols } from "../../../../../../components/ui/Icon";
import { Panel } from "../../../../../../components/ui/Panel";
import { PaginationModel } from "../../../../../../components/widgets/pagination/Pagination_model";
import { LOCAL_STORAGE_FILTERS } from "../../../../../../constants";
import {
  FilterModel,
  FilterOperator,
  IFilterAttribute,
  IFilterModel
} from "../../../../../../core/filter/Filter_model";
import ProgressIndicatorModel, {
  ProgressIndicatorModel as IProgressIndicatorModel
} from "../../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import I18n from "../../../../../../core/localization/I18n";
import { Animations } from "../../../../../../core/util/Animations";
import { Enums } from "../../../../../../enums";
import { IFilteredOptions } from "../../../../../../services/api/v2/filteredApi/FilteredApiModel";
import ProjectTeamUserPermissionsApi, {
  ProjectTeamUserPermissionsApi as IProjectTeamUserPermissionsApi
} from "../../../../../../services/api/v2/projectTeamUserPermissions/ProjectTeamUserPermissions.api";
import { GetProjectTeamUserPermissionsColumns } from "./ProjectTeamUsersPermissions_columns";
import { GetProjectTeamUserFilterFields } from "./ProjectTeamUsersPermissions_fields";
import { ProjectTeamUserPermissionForm } from "./ProjectTeamUsersPermissions_form";
import { IModalContextModel } from "../../../../../../core/modalZ/context/IModalContext";
import ModalContext from "../../../../../../core/modalZ/context/ModalContext";

export class ProjectTeamUsersPermissionsModel {
  filterModel: IFilterModel<FP.Entities.IProjecTeamUserPermission>;
  @observable isLoading: boolean = true;
  @observable users: FP.Entities.IProjecTeamUserPermission[];
  paginationModel;
  organisationId: number;
  projectId: number;
  modalService: IModalContextModel;
  userPemri;
  smartTableConfig: any;
  userPermissionsProvider: IProjectTeamUserPermissionsApi;
  productTourSteps: any;
  httpProgress: IProgressIndicatorModel;
  showTour: boolean;

  /**
   *
   */
  constructor(organisationId: number, projectId: number) {
    makeObservable(this);
    this.organisationId = organisationId;
    this.projectId = projectId;
    this.httpProgress = ProgressIndicatorModel;
    this.modalService = ModalContext;
    this.userPermissionsProvider = ProjectTeamUserPermissionsApi;
    this.smartTableConfig = GetProjectTeamUserPermissionsColumns(organisationId, this);
    this.installPagination();
    this.installFilter();
    this.setupTourSteps();
    this.showTour = false;
  }

  installFilter = () => {
    let config = {
      paginationModel: this.paginationModel,
      initOpts: {
        filterCb: async (filters: Partial<IFilteredOptions>) => {
          return await this.userPermissionsProvider.getUsersInProject(this.organisationId, this.projectId, filters);
        }
      },
      localStorageName: LOCAL_STORAGE_FILTERS.PROJECT_TEAM_USERS
    };
    this.filterModel = new FilterModel(config);

    const lifeCycleFilter: IFilterAttribute = {
      key: "lifecycleStatus",
      value: [Enums.LifecycleStatus.Active + ""],
      isHidden: true,
      operator: FilterOperator.EQUALS
    };

    const nameFilter: IFilterAttribute = {
      key: "userId",
      label: I18n.t("filters.user"),
      value: [],
      operator: FilterOperator.EQUALS,
      extractFilterValue: value => {
        return value.map(e => e.id);
      },
      valueRenderer: (e: any, s) => {
        return `${e.firstName} ${e.lastName}`;
      }
    };

    const permissionLevelFilter: IFilterAttribute = {
      key: "permissionLevel",
      label: I18n.t("filters.permissionLevel"),
      value: [],
      operator: FilterOperator.EQUALS,
      extractFilterValue: value => {
        return value.map(e => e.key);
      },
      valueRenderer: (e: any, s) => {
        return `${e.label}`;
      }
    };

    const searchPhraseFilter: IFilterAttribute = {
      key: "searchPhrase",
      label: I18n.t("filters.searchPhrase"),
      value: [],
      operator: FilterOperator.CONTAINS
    };

    this.filterModel.addSort({
      key: "id",
      isAsc: true
    });

    this.filterModel.addFilter(lifeCycleFilter);
    this.filterModel.addFilter(nameFilter);
    this.filterModel.addFilter(permissionLevelFilter);
    this.filterModel.addFilter(searchPhraseFilter);
    this.filterModel.setConfig({
      formFields: GetProjectTeamUserFilterFields(this.organisationId, this.projectId),
      onDataLoaded: d => this.setUsers(d)
    });
  };

  installPagination = () => {
    this.paginationModel = new PaginationModel();
    this.paginationModel.setConfig({ onPageClick: this.loadPageData });
  };
  loadPageData = (index: number) => {
    this.filterModel.setConfig({ page: index });
    this.filterModel.loadData();
  };

  @action
  onSearchChange = (ev: React.SyntheticEvent) => {
    let e = ev.currentTarget as HTMLInputElement;
    this.filterModel.setFilterValue("searchPhrase", e.value);
  };

  @action
  resetNameFilter = () => {
    this.filterModel.setFilterValue("name", "");
  };

  onMount = (organisationId: number) => {
    this.loadUsers();
  };

  onUnmount = () => {};

  @action
  loadUsers = async () => {
    await this.filterModel.loadData();
  };

  addUser = async users => {
    this.httpProgress.showTopProgressBarVisible();
    let res = await this.userPermissionsProvider.create(this.organisationId, this.projectId, users);
    if (!res) return;
    this.loadUsers();
    this.httpProgress.hideTopProgressBarVisible();
  };

  updateUserPermission = async (user: FP.Entities.IUserPermission) => {
    this.httpProgress.showTopProgressBarVisible();
    let res = await this.userPermissionsProvider.update(this.organisationId, this.projectId, user.userId, user);
    if (!res) return;
    await this.loadUsers();
    this.httpProgress.hideTopProgressBarVisible();
  };

  removeUser = async (userId: number) => {
    this.httpProgress.showTopProgressBarVisible();
    let res = await this.userPermissionsProvider.remove(this.organisationId, this.projectId, userId);

    if (!res) return;
    this.loadUsers();
    this.httpProgress.hideTopProgressBarVisible();
  };

  @action
  setUsers = users => {
    this.isLoading = true;
    this.users = users || this.users;
    this.isLoading = false;
  };

  showConfirmDeleteModal = item => {
    return new Promise(resolve => {
      this.modalService.showConfirmDialog(
        <h1 className="mt-4">{I18n.t("phrases.confirm")}</h1>,
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <Icon symbol={IconSymbols.AlertCircle} className="mr-2" />
              {I18n.t("warnings.removeProjectTeamUser")}
            </div>
          </div>
          <div className="row">
            <div className="col">{I18n.t("phrases.confirmUserRemove", { name: item.name })}</div>
          </div>
        </div>,
        I18n.t("phrases.yes"),
        I18n.t("phrases.no"),
        {
          wrapWidth: "small",
          spacing: "small",
          position: "middle",
          panelProps: {
            background: Panel.PanelBackgrounds.BG_WHITE
          }
        },
        async () => {
          this.modalService.hide();
          await this.removeUser(item.userId);
          this.loadUsers();
          resolve(true);
        },
        () => {
          this.modalService.hide();
        },
        ButtonTypes.DANGER
      );
    });
  };

  showAddProjectTeamUserForm = () => {
    this.modalService.show({
      showClose: true,
      title: null,
      content: (
        <ProjectTeamUserPermissionForm
          projectId={this.projectId}
          addUserToProject={users => {
            this.addUser(users);
            this.modalService.hide();
          }}
          hideModal={() => this.modalService.hide()}
        />
      ),
      componentProps: {
        wrapHeight: "full",
        wrapWidth: "small",
        position: "right"
      },
      animationOptions: {
        animateIn: Animations.SLIDE_IN_RIGHT,
        animateOut: Animations.SLIDE_OUT_RIGHT,
        speed: 5
      },
      actions: []
    });
  };

  setupTourSteps = () => {
    this.productTourSteps = [];
  };
}
